import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { TrendingUp, IndianRupee, Briefcase, Clock, Building2, Users, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { isManagerRole, isAdmin as isAdminRole } from '../lib/roles';
import { CreatorEarningsPanel } from '../components/creator/CreatorEarningsPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Dashboard = () => {
  const { user } = useAuth();
  const isAdmin = isManagerRole(user);
  const isTrueAdmin = isAdminRole(user);

  const [analytics, setAnalytics] = useState(null);
  const [platformStats, setPlatformStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'brand') return;
    fetchAnalytics();
    if (isTrueAdmin) fetchPlatformStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Brands have their own dashboard
  if (user?.role === 'brand') {
    return <Navigate to="/brand/dashboard" replace />;
  }

  const fetchAnalytics = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/analytics/dashboard`, {
        withCredentials: true
      });
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatformStats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/platform-stats`, {
        withCredentials: true,
      });
      setPlatformStats(data);
    } catch (error) {
      // Silent — not fatal; card will show 0.
    }
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]">
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#FF451A] border-r-transparent"></div>
          </div>
        </div>
      </div>
    );
  }

  const statCards = isTrueAdmin ? [
    {
      label: 'Active Campaigns',
      value: platformStats?.active_campaigns || 0,
      icon: Briefcase,
      color: '#0055FF'
    },
    {
      label: 'Creators In Pipeline',
      value: platformStats?.creators_in_pipeline || 0,
      icon: Users,
      color: '#FF451A'
    },
    {
      label: 'Total Brands',
      value: platformStats?.total_brands || 0,
      icon: Building2,
      color: '#00C972'
    },
    {
      label: 'Pending Approvals',
      value: platformStats?.pending_approvals || 0,
      icon: CheckCircle2,
      color: '#FFB800'
    }
  ] : isAdmin ? [
    {
      label: 'Total Campaigns',
      value: analytics?.total_campaigns || 0,
      icon: Briefcase,
      color: '#0055FF'
    },
    {
      label: 'Pitched',
      value: (analytics?.stage_distribution?.pitched || 0),
      icon: TrendingUp,
      color: '#0055FF'
    },
    {
      label: 'Locked',
      value: analytics?.won_campaigns || 0,
      icon: IndianRupee,
      color: '#00C972'
    },
    {
      label: 'Total Earnings',
      value: `₹${analytics?.total_earnings?.toLocaleString() || 0}`,
      icon: IndianRupee,
      color: '#FF451A'
    }
  ] : [
    {
      label: 'Pitched Campaigns',
      value: (analytics?.stage_distribution?.pitched || 0),
      icon: Briefcase,
      color: '#0055FF'
    },
    {
      label: 'Locked Campaigns',
      value: analytics?.won_campaigns || 0,
      icon: IndianRupee,
      color: '#00C972'
    }
  ];

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="dashboard-page">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2">Dashboard</h1>
            <p className="text-[#6B6B6B] dark:text-zinc-400">Welcome back! Here's your overview.</p>
          </div>

          <div className={`grid grid-cols-1 md:grid-cols-2 ${isAdmin ? 'lg:grid-cols-4' : ''} gap-6 mb-8`}>
            {statCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <div
                  key={index}
                  className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-6 hover:-translate-y-[1px] transition-transform duration-200"
                  data-testid={`stat-card-${card.label.toLowerCase().replace(/ /g, '-')}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${card.color}15` }}
                    >
                      <Icon size={20} strokeWidth={1.5} style={{ color: card.color }} />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-[#0F0F0F] dark:text-white mb-1">{card.value}</div>
                  <div className="text-sm text-[#6B6B6B] dark:text-zinc-400">{card.label}</div>
                </div>
              );
            })}
          </div>

          {/* Creator-only earnings section (additive; hidden for manager/admin) */}
          {user?.role === 'creator' && (
            <div className="mb-8">
              <CreatorEarningsPanel />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-6">
              <h3 className="text-xl font-semibold text-[#0F0F0F] dark:text-white mb-4">Campaign Overview</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[#6B6B6B] dark:text-zinc-400">Total Campaigns</span>
                  <span className="font-semibold text-[#0F0F0F] dark:text-white" data-testid="total-campaigns-count">{analytics?.total_campaigns || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#6B6B6B] dark:text-zinc-400">Pitched</span>
                  <span className="font-semibold text-[#0055FF]">{analytics?.stage_distribution?.pitched || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#6B6B6B] dark:text-zinc-400">Locked</span>
                  <span className="font-semibold text-[#00C972]">{analytics?.won_campaigns || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#6B6B6B] dark:text-zinc-400">Total Invoices</span>
                  <span className="font-semibold text-[#0F0F0F] dark:text-white">{analytics?.total_invoices || 0}</span>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-6">
              <h3 className="text-xl font-semibold text-[#0F0F0F] dark:text-white mb-4">Conversion</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between items-baseline mb-2">
                    <span className="text-[#6B6B6B] dark:text-zinc-400">Pitched → Locked Rate</span>
                    <span className="text-2xl font-bold text-[#FF451A]">{analytics?.conversion_rate || 0}%</span>
                  </div>
                  <div className="w-full bg-white dark:bg-[#0A0B0D] rounded-full h-2 overflow-hidden">
                    <div
                      className="h-2 bg-[#FF451A] rounded-full transition-all"
                      style={{ width: `${analytics?.conversion_rate || 0}%` }}
                    ></div>
                  </div>
                </div>
                {isAdmin && analytics?.managed_creators_count > 0 && (
                  <div className="flex justify-between items-center pt-3 border-t border-black/[0.08] dark:border-white/5">
                    <span className="text-[#6B6B6B] dark:text-zinc-400">Active Creators</span>
                    <span className="font-semibold text-[#0F0F0F] dark:text-white">{analytics.managed_creators_count}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-6">
            <h3 className="text-xl font-semibold text-[#0F0F0F] dark:text-white mb-2">Quick Actions</h3>
            <p className="text-[#6B6B6B] dark:text-zinc-400 mb-4">
              {isAdmin ? 'Manage your roster and campaigns' : 'View your campaigns and invoices'}
            </p>
            <div className="flex flex-wrap gap-3">
              {isAdmin && (
                <Link
                  to="/campaigns"
                  className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-6 py-2.5 font-medium hover:bg-[#E63E17] transition-colors focus:ring-2 focus:ring-[#FF451A]/30"
                  data-testid="create-campaign-button"
                >
                  Create Campaign
                </Link>
              )}
              {!isAdmin && (
                <Link
                  to="/campaigns"
                  className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-6 py-2.5 font-medium hover:bg-[#E63E17] transition-colors focus:ring-2 focus:ring-[#FF451A]/30"
                  data-testid="view-campaigns-button"
                >
                  View My Campaigns
                </Link>
              )}
              <Link
                to="/invoices"
                className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-6 py-2.5 font-medium transition-colors"
                data-testid="generate-invoice-button"
              >
                Generate Invoice
              </Link>
              <Link
                to="/content-ideas"
                className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-6 py-2.5 font-medium transition-colors"
                data-testid="explore-content-button"
              >
                Content Ideas
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
